import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import queryString from "query-string";
import logger from "../../../utils/logger";
import bg from "../../../assets/img/spacia/web/Background.png";
import logo from "../../../assets/img/spacia/web/spacia_logo.png";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";

import { apiV2Service } from "../../../services/apiV2Service";
import { showApiMessage } from "../../../utils/messageHandler";

const VerifyEmail = () => {
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get("code") || searchParams.get("token");

  useEffect(() => {
    const performVerification = async () => {
      if (!code) {
        setVerifying(false);
        setError("Verification code is missing.");
        return;
      }

      try {
        logger.log("Attempting email confirmation with code:", code);
        const response = await apiV2Service.confirmEmail(code);
        logger.log("Email confirmation response status:", response.status);

        // Any 2xx status code is a success response for this endpoint
        if (response.status >= 200 && response.status < 300) {
          setSuccess(true);
          showApiMessage(response, "confirm email");
        } else {
          setError("Verification failed with status: " + response.status);
        }
      } catch (err: any) {
        logger.error("Email verification error:", err);

        // Extract error message from API response if available
        const apiError = err?.response?.data;
        if (apiError?.message) {
          setError(apiError.message);
        } else if (apiError?.error === "NOT_FOUND") {
          setError("The verification link is invalid or has expired.");
        } else {
          setError("An error occurred during verification. Please try again.");
        }

        showApiMessage(err, "confirm email");
      } finally {
        setVerifying(false);
      }
    };

    performVerification();
  }, [code]);

  return (
    <div className="flex relative justify-center items-center w-full min-h-screen">
      <div
        className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
        style={{ backgroundImage: `url(${bg})` }}
      ></div>

      <div className="absolute top-8 left-8 z-10">
        <Link to="/home">
          <img src={logo} alt="Spacia Logo" className="h-10 md:h-12" />
        </Link>
      </div>

      <div className="relative z-10 p-6 mx-auto w-full max-w-md bg-white rounded-lg shadow-lg md:p-8">
        <div className="text-center">
          {verifying ? (
            <div className="space-y-4">
              <div className="flex justify-center">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
              </div>
              <h1 className="text-xl font-semibold text-gray-800 md:text-2xl">Verifying Email...</h1>
              <p className="text-gray-600">Please wait while we verify your new email address.</p>
            </div>
          ) : success ? (
            <div className="space-y-4">
              <div className="flex justify-center">
                <CheckCircle className="w-16 h-16 text-green-500" />
              </div>
              <h1 className="text-xl font-semibold text-gray-800 md:text-2xl">Email Verified!</h1>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                Your email has been successfully updated and verified.
              </div>
              <div className="pt-4">
                <Link
                  to="/signin"
                  className="block w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
                >
                  Continue to Sign In
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <AlertCircle className="w-16 h-16 text-red-500" />
              </div>
              <h1 className="text-xl font-semibold text-gray-800 md:text-2xl">Verification Failed</h1>
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                {error || "We couldn't verify your email address. The link may have expired or is invalid."}
              </div>
              <div className="pt-4">
                <Link
                  to="/signin"
                  className="block w-full py-2 px-4 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-md transition-colors"
                >
                  Back to Sign In
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 text-center border-t pt-6">
          <p className="text-xs text-gray-500">If you're having trouble, please contact our support team.</p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
